import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Home as HomeIcon, ShoppingCart, User, Shield, Trophy, Package } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../hooks/useI18n';
import axios from 'axios';
import { API } from '../config';

export const MobileNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useI18n();
  const [cartCount, setCartCount] = useState(0);
  const [pendingInventory, setPendingInventory] = useState(0);

  const fetchCartCount = async () => {
    try {
      const { data } = await axios.get(`${API}/cart`, { withCredentials: true });
      const count = data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
      setCartCount(count);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    }
  };

  const fetchPendingInventory = async () => {
    try {
      const { data } = await axios.get(`${API}/kuji/inventory/grouped`, {
        params: { limit: 1 },
        withCredentials: true,
      });
      const count = data?.tier_counts?.all || 0;
      setPendingInventory(count);
    } catch (error) {
      // 401 just means not authed — silent
    }
  };

  useEffect(() => {
    if (user) {
      fetchCartCount();
      fetchPendingInventory();
    }
  }, [user, location.pathname]);

  // Refresh pending inventory count on global trigger (e.g. after winning a Kuji draw)
  useEffect(() => {
    const onUpdate = () => fetchPendingInventory();
    window.addEventListener('inventory-updated', onUpdate);
    return () => window.removeEventListener('inventory-updated', onUpdate);
  }, []);

  const navItems = [
    { icon: HomeIcon, label: t('home'), path: '/', testId: 'nav-home' },
    { icon: Trophy, label: 'Kuji', path: '/kuji', testId: 'nav-kuji' },
    { icon: Package, label: 'Inventori', path: '/inventory', testId: 'nav-inventory', badge: pendingInventory },
    { icon: ShoppingCart, label: t('cart'), path: '/cart', testId: 'nav-cart', badge: cartCount },
    { icon: User, label: t('profile'), path: '/profile', testId: 'nav-profile' },
  ];

  if (user?.role === 'admin') {
    navItems.push({ icon: Shield, label: t('admin'), path: '/admin', testId: 'nav-admin' });
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 backdrop-blur-2xl pb-safe pt-3 px-6 flex justify-between items-center z-50" style={{ background: 'rgba(8, 12, 10, 0.9)', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
      {navItems.map((item) => {
        const isActive = location.pathname === item.path;
        const Icon = item.icon;
        
        return (
          <motion.button
            key={item.path}
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate(item.path)}
            className="flex flex-col items-center gap-1 relative py-2"
            data-testid={item.testId}
          >
            <div className="relative">
              <Icon className="w-6 h-6" style={{ color: isActive ? '#D4AF37' : '#A3B8AC' }} />
              {item.badge > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-2 -right-2 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
                  style={{ background: '#D4AF37', color: '#080C0A' }}
                  data-testid="cart-badge"
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </motion.span>
              )}
            </div>
            <span className="text-xs font-medium" style={{ color: isActive ? '#D4AF37' : '#A3B8AC' }}>
              {item.label}
            </span>
          </motion.button>
        );
      })}
    </nav>
  );
};